import React from 'react';
import { TrendingUp, TrendingDown, Zap, Users, Building2, Stethoscope } from 'lucide-react';

const ROISection = ({ isDark }) => {
  const metrics = [
    {
      value: '+40%',
      label: 'Patient Throughput',
      description: 'Increase in daily consultations',
      icon: TrendingUp,
      color: 'emerald'
    },
    {
      value: '-70%',
      label: 'Admin Workload',
      description: 'Reduction in manual tasks',
      icon: TrendingDown,
      color: 'blue'
    },
    {
      value: '80%',
      label: 'Documentation Time',
      description: 'Reduction in note-taking',
      icon: Zap,
      color: 'purple'
    }
  ];

  const comparisons = [
    { before: 'Manual Entry', after: 'Automated', category: 'Documentation' },
    { before: 'Fragmented', after: 'Unified', category: 'Workflow' },
    { before: 'Reactive', after: 'Proactive', category: 'Care Delivery' },
    { before: 'Revenue Leakage', after: 'Full Capture', category: 'Billing' }
  ];

  const personas = [
    {
      icon: Stethoscope,
      title: 'For Doctors',
      benefits: ['Less documentation time', 'More patient time', 'Reduced burnout']
    },
    {
      icon: Building2,
      title: 'For Clinics',
      benefits: ['Higher throughput', 'Lower admin costs', 'Standardized care']
    },
    {
      icon: Users,
      title: 'For Hospitals',
      benefits: ['Enterprise scalability', 'Revenue optimization', 'Quality metrics']
    }
  ];

  return (
    <section 
      id="roi" 
      className={`section-padding ${isDark ? 'bg-slate-900' : 'bg-white'}`}
      data-testid="roi-section"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <span className={`inline-block px-4 py-2 rounded-full text-sm font-medium mb-4 ${
            isDark ? 'bg-emerald-900/50 text-emerald-400' : 'bg-emerald-100 text-emerald-700'
          }`}>
            ROI & Impact
          </span>
          <h2 className={`text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 ${
            isDark ? 'text-white' : 'text-slate-900'
          }`}>
            Clinical Impact Meets Financial Outcomes
          </h2>
          <p className={`text-lg max-w-2xl mx-auto ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
            Built for clinical outcomes AND revenue growth. Measure the transformation.
          </p>
        </div>

        {/* Metrics */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {metrics.map((metric, index) => (
            <div 
              key={index}
              className={`text-center p-8 rounded-2xl ${
                isDark ? 'bg-slate-800' : 'bg-slate-100'
              }`}
              data-testid={`metric-${index}`}
            >
              <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4 ${
                metric.color === 'emerald' ? (isDark ? 'bg-emerald-900/50' : 'bg-emerald-100') :
                metric.color === 'blue' ? (isDark ? 'bg-blue-900/50' : 'bg-blue-100') : 
                (isDark ? 'bg-purple-900/50' : 'bg-purple-100')
              }`}>
                <metric.icon className={`w-8 h-8 ${
                  metric.color === 'emerald' ? 'text-emerald-500' :
                  metric.color === 'blue' ? 'text-blue-500' : 'text-purple-500'
                }`} />
              </div>
              <p className={`text-5xl font-bold mb-2 ${
                metric.color === 'emerald' ? 'text-emerald-500' :
                metric.color === 'blue' ? 'text-blue-500' : 'text-purple-500'
              }`}>
                {metric.value}
              </p>
              <p className={`text-xl font-semibold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                {metric.label}
              </p>
              <p className={isDark ? 'text-slate-400' : 'text-slate-600'}>{metric.description}</p>
            </div>
          ))}
        </div>

        {/* Before vs After */}
        <div className={`rounded-2xl p-8 md:p-12 mb-16 ${isDark ? 'bg-slate-800' : 'bg-slate-900'}`}>
          <h3 className="text-2xl font-bold text-white text-center mb-8">
            Before Skydoc vs. After Skydoc
          </h3>
          <div className="grid md:grid-cols-4 gap-4">
            {comparisons.map((item, index) => (
              <div key={index} className="text-center">
                <p className="text-sm text-slate-400 mb-2">{item.category}</p>
                <div className="flex items-center justify-center gap-3">
                  <span className="text-slate-400 line-through">{item.before}</span>
                  <span className="text-emerald-400">→</span>
                  <span className="text-white font-semibold">{item.after}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Persona benefits */}
        <div className="grid md:grid-cols-3 gap-6">
          {personas.map((persona, index) => (
            <div 
              key={index}
              className={`p-6 rounded-xl border ${
                isDark 
                  ? 'bg-slate-800 border-slate-700' 
                  : 'bg-white border-slate-200'
              }`}
              data-testid={`persona-${index}`}
            >
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                isDark ? 'bg-blue-900/50' : 'bg-blue-100'
              }`}>
                <persona.icon className="w-6 h-6 text-blue-500" />
              </div>
              <h4 className={`text-lg font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                {persona.title}
              </h4>
              <ul className="space-y-2">
                {persona.benefits.map((benefit, i) => (
                  <li key={i} className={`flex items-center gap-2 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                    <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ROISection;
